import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HomepageSettings } from '@rivethealth/data/user';
import { Permission } from '@rivethealth/data/user-account';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { AuthStore } from './auth';
import { CellStore } from './cell';
import { API_URL, ROUTER_API_URL } from './common';

export type User = ChangeUser & {
  permissionLevel: PermissionLevel;
};

export type SavedUser = User & { id: number; disabled: boolean };

export type ChangeUser = {
  name: string;
  email: string;
  externalId?: string;
  phone?: string;
  fax?: string;
  title?: string;
  disabled?: boolean;
  businessUnitPermissions: number[] | null;
  notificationContact?: boolean;
  alternateEmail?: string;
};

export enum PermissionLevel {
  ADMIN = 'admin',
  USER = 'user',
}

export namespace PermissionLevel {
  export function display(value: PermissionLevel): string {
    switch (value) {
      case PermissionLevel.ADMIN:
        return 'Admin';
      case PermissionLevel.USER:
        return 'User';
    }
  }

  export const values = [PermissionLevel.ADMIN, PermissionLevel.USER];
}

@Injectable({ providedIn: 'root' })
export class RouterUserService {
  constructor(
    @Inject(ROUTER_API_URL) private readonly routerApiUrl: string,
    private readonly http: HttpClient,
  ) {}

  // should we share this code and cache?  if router and cell are
  // in sync it should be fine...
  private userCache: Map<string, User & { type: 'system' | 'user' }> =
    new Map();
  private userFetchObservables: Map<
    string,
    Observable<User & { type: 'system' | 'user' }>
  > = new Map();
  clearUserCache(): void {
    this.userCache.clear();
  }
  getUser(userId: number) {
    const userCacheKey = `${userId}`;
    const cachedUser = this.userCache.get(userCacheKey);
    if (cachedUser) {
      return of(cachedUser);
    }

    const ongoingFetch = this.userFetchObservables.get(userCacheKey);
    if (ongoingFetch) {
      return ongoingFetch;
    }

    const userObservable = this.http
      .get<
        User & { type: 'system' | 'user' }
      >(`${this.routerApiUrl}/users/${userId}`)
      .pipe(
        tap(user => {
          this.userCache.set(userCacheKey, user);
          this.userFetchObservables.delete(userCacheKey);
        }),
        shareReplay(1),
      );

    this.userFetchObservables.set(userCacheKey, userObservable);
    return userObservable;
  }

  getIntercomHash(userId: number) {
    return this.http.get<{ userHash: string }>(
      `${this.routerApiUrl}/users/${userId}/intercomHash`,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(
    @Inject(API_URL) private readonly apiUrl: string,
    @Inject(ROUTER_API_URL) private readonly routerApiUrl: string,
    private readonly http: HttpClient,
    private readonly auth: AuthStore,
    private readonly cellStore: CellStore,
  ) {}

  createUser(accountId: number, user: User) {
    this.clearUserCache();
    this.clearUsersCache();
    return this.http.post<User & { id: number }>(
      `${this.apiUrl}/users?accountId=${accountId}`,
      user,
    );
  }

  private userCache: Map<string, User & { type: 'system' | 'user' }> =
    new Map();
  private userFetchObservables: Map<
    string,
    Observable<User & { type: 'system' | 'user' }>
  > = new Map();
  clearUserCache(): void {
    this.userCache.clear();
  }
  getUser(userId: number) {
    const userCacheKey = `${userId}`;
    const cachedUser = this.userCache.get(userCacheKey);
    if (cachedUser) {
      return of(cachedUser);
    }

    const ongoingFetch = this.userFetchObservables.get(userCacheKey);
    if (ongoingFetch) {
      return ongoingFetch;
    }

    const userObservable = this.http
      .get<User & { type: 'system' | 'user' }>(`${this.apiUrl}/users/${userId}`)
      .pipe(
        tap(user => {
          this.userCache.set(userCacheKey, user);
          this.userFetchObservables.delete(userCacheKey);
        }),
        shareReplay(1),
      );

    this.userFetchObservables.set(userCacheKey, userObservable);
    return userObservable;
  }

  getUserByAccount(userId: number, accountId: number) {
    return this.http.get<User & { type: 'system' | 'user' }>(
      `${this.apiUrl}/users/${userId}/account/${accountId}`,
    );
  }

  getFirstUse(userId: number) {
    return this.http.get<{ firstUse: boolean }>(
      `${this.apiUrl}/users/first-use/${userId}`,
    );
  }

  private usersCache: Map<string, (SavedUser & { type: 'system' | 'user' })[]> =
    new Map();
  private usersFetchObservables: Map<
    string,
    Observable<(SavedUser & { type: 'system' | 'user' })[]>
  > = new Map();
  clearUsersCache(): void {
    this.usersCache.clear();
  }

  getUsers(
    accountId: number,
    includeDisabled = false,
    permissionRequired: Permission[] = [],
    orderByName = false,
  ) {
    const userCacheKey = `${accountId}-${includeDisabled}-${permissionRequired.join(
      ',',
    )}-${orderByName}`;
    const cachedUsers = this.usersCache.get(userCacheKey);
    if (cachedUsers) {
      return of(cachedUsers);
    }

    const ongoingFetch = this.usersFetchObservables.get(userCacheKey);
    if (ongoingFetch) {
      return ongoingFetch;
    }

    const usersObservable = this.http
      .get<(SavedUser & { type: 'system' | 'user' })[]>(
        `${this.apiUrl}/users`,
        {
          params: {
            accountId: accountId.toString(),
            includeDisabled: String(includeDisabled),
            permissionRequired: encodeURIComponent(
              permissionRequired.join('::'),
            ),
            orderByName,
          },
        },
      )
      .pipe(
        tap(users => {
          this.usersCache.set(userCacheKey, users);
          this.usersFetchObservables.delete(userCacheKey);
        }),
        shareReplay(1),
      );

    this.usersFetchObservables.set(userCacheKey, usersObservable);
    return usersObservable;
  }

  closeBillboard(userId: number, billboardId: string) {
    return this.http.post<void>(
      `${this.apiUrl}/users/${userId}/close-billboard`,
      { billboardId },
    );
  }

  billboardStatus(userId: number, billboardId: string) {
    return this.http.get<{ show: boolean }>(
      `${this.apiUrl}/users/${userId}/billboard`,
      {
        params: {
          billboardId: billboardId,
        },
      },
    );
  }

  modifyUser(accountId: number, userId: number, user: ChangeUser) {
    this.clearUserCache();
    this.clearUsersCache();
    return this.http.put(
      `${this.apiUrl}/accounts/${accountId}/users/${userId}`,
      user,
    );
  }

  getCsv(accountId: number, envIsZelis: string) {
    const url = new URL(this.cellStore.getCellUrl() || this.apiUrl);
    url.pathname = '/users/csv';
    url.searchParams.append('Authorization', `Bearer ${this.auth.getToken()}`);
    url.searchParams.append('accountId', accountId.toString());
    url.searchParams.append('envIsZelis', envIsZelis.toString());
    return url.toString();
  }

  getIntercomHash(userId: number) {
    return this.http.get<{ userHash: string }>(
      `${this.routerApiUrl}/users/${userId}/intercomHash`,
    );
  }

  checkDefaultAssignment(userId: number, accountId: number) {
    return this.http.get<boolean>(
      `${this.apiUrl}/users/${userId}/defaultAssignee`,
      {
        params: {
          accountId: accountId,
        },
      },
    );
  }

  getCannySsoToken(userId: number) {
    return this.http.get<string>(
      `${this.routerApiUrl}/users/${userId}/cannySsoToken`,
    );
  }

  setNotificationContacts(accountId: number, usersToNotify: string[]) {
    return this.http.post(
      `${this.apiUrl}/users/notification-contact`,
      usersToNotify,
      {
        params: { accountId: accountId.toString() },
      },
    );
  }

  getHomepageSettings(accountId: number, userId: number) {
    return this.http.get<HomepageSettings | null>(
      `${this.apiUrl}/users/${userId}/homepage`,
      {
        params: {
          accountId,
        },
      },
    );
  }

  deleteHomepageSettings(accountId: number, userId: number) {
    return this.http.delete(`${this.apiUrl}/users/${userId}/homepage`, {
      params: {
        accountId,
      },
    });
  }

  updateHomepageSettings(
    accountId: number,
    userId: number,
    settings: HomepageSettings,
  ) {
    return this.http.post(`${this.apiUrl}/users/${userId}/homepage`, settings, {
      params: {
        accountId,
      },
    });
  }

  invalidatePassword(userId: number) {
    return this.http.put(`${this.apiUrl}/users/${userId}/password-invalid`, {});
  }
}
